import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { Star } from "lucide-react";
import client from "../../api/client";
import { apiUpload } from "../../utils/apiProxy";
import "./SendToClientPopup.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * «Скинуть клиенту»: редактор выбирает версию (отмеченные звёздочкой —
 * сверху), смотрит обложку и листает книгу, при желании подменяет книгу или
 * обложку своими PDF и отправляет. Бэк (POST /api/proofs/admin) сохраняет
 * снимок — клиент увидит его в кабинете разворотами и утвердит или пришлёт
 * правки.
 */

interface VersionRow {
  id: number;
  editor?: string;
  createdAt: string;
  starred?: boolean;
}

export interface SendToClientPopupProps {
  userId: number | string;
  username?: string;
  /** Какую версию выбрать сразу (например, открытую в редакторе). */
  initialVersionId?: number | null;
  onClose: () => void;
  /** notifyStatus — сообщение клиенту в Snippex: sent | no_contact | pending | failed | null (выключено). */
  onSent?: (notifyStatus: string | null) => void;
}

const fmtDate = (d: string) =>
  new Date(d).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Разворот книги: 0 — [пусто, стр. 1], k — [2k, 2k+1]. Как в кабинете клиента. */
export const spreadPages = (spread: number, total: number): [number | null, number | null] => {
  const l = spread * 2;
  const r = spread * 2 + 1;
  return [l >= 1 && l <= total ? l : null, r >= 1 && r <= total ? r : null];
};
export const spreadCount = (total: number) => (total > 0 ? Math.ceil((total + 1) / 2) : 0);

export const SendToClientPopup: React.FC<SendToClientPopupProps> = ({
  userId,
  username,
  initialVersionId,
  onClose,
  onSent,
}) => {
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  // null — оригинал (исходные ответы клиента).
  const [selected, setSelected] = useState<number | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [bookUrl, setBookUrl] = useState<string | null>(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [coverState, setCoverState] = useState<"idle" | "loading" | "none" | "error">("idle");

  // Свои файлы поверх выбранной версии.
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const bookInput = useRef<HTMLInputElement | null>(null);
  const coverInput = useRef<HTMLInputElement | null>(null);

  const [spread, setSpread] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Шаблон книги и версии.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: info }, { data: list }] = await Promise.all([
          client.get("/proofs/admin", { params: { userId } }),
          client.get("/versions", { params: { userId } }),
        ]);
        if (cancelled) return;
        if (!info.templateId) {
          setLoadError("У пользователя нет книги");
          return;
        }
        setTemplateId(info.templateId);
        const rows: VersionRow[] = (list as any[]).map((v) => ({
          id: v.id,
          editor: v.editor,
          createdAt: v.createdAt,
          starred: !!v.starred,
        }));
        setVersions(rows);
        const starred = rows.find((v) => v.starred);
        setSelected(
          initialVersionId !== undefined && initialVersionId !== null && rows.some((v) => v.id === initialVersionId)
            ? initialVersionId
            : starred?.id ?? rows[0]?.id ?? null
        );
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.response?.data?.error || "Не удалось загрузить версии");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, initialVersionId]);

  const ordered = useMemo(
    () => [...versions].sort((a, b) => Number(!!b.starred) - Number(!!a.starred) || +new Date(b.createdAt) - +new Date(a.createdAt)),
    [versions]
  );

  // Книга: свой файл или сборка выбранной версии на бэке.
  useEffect(() => {
    if (selected === undefined || !templateId) return;
    let cancelled = false;
    let url: string | null = null;
    setSpread(0);
    setNumPages(0);
    setBookError(null);
    (async () => {
      if (bookFile) {
        url = URL.createObjectURL(bookFile);
        setBookUrl(url);
        return;
      }
      setBookLoading(true);
      setBookUrl(null);
      try {
        const qs = selected ? `?versionId=${selected}` : "";
        const res = await client.get(`/pdf/admin/${userId}/${templateId}${qs}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBookUrl(url);
      } catch {
        if (!cancelled) setBookError("Не удалось собрать книгу");
      } finally {
        if (!cancelled) setBookLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selected, templateId, userId, bookFile]);

  // Обложка: свой файл или сборка по числу страниц книги (корешок).
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    let url: string | null = null;
    (async () => {
      if (coverFile) {
        url = URL.createObjectURL(coverFile);
        setCoverUrl(url);
        setCoverState("idle");
        return;
      }
      if (!numPages) return;
      setCoverState("loading");
      setCoverUrl(null);
      try {
        const res = await client.get(`/pdf/admin/${userId}/${templateId}/cover`, {
          params: { pageCount: numPages },
          responseType: "blob",
        });
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setCoverUrl(url);
        setCoverState("idle");
      } catch (e: any) {
        if (!cancelled) setCoverState(e?.response?.status === 404 ? "none" : "error");
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [templateId, userId, numPages, coverFile]);

  const upload = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiUpload("/upload", fd);
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    const data = await res.json();
    if (!data?.url) throw new Error("upload failed");
    return data.url;
  };

  const handleSend = async () => {
    if (!numPages) {
      setError("Книга ещё не загрузилась");
      return;
    }
    setError(null);
    setSending(true);
    try {
      const [bookPdfUrl, coverPdfUrl] = await Promise.all([
        bookFile ? upload(bookFile) : Promise.resolve(undefined),
        coverFile ? upload(coverFile) : Promise.resolve(undefined),
      ]);
      const { data } = await client.post("/proofs/admin", {
        userId: Number(userId),
        versionId: bookFile ? null : selected,
        pages: numPages,
        bookPdfUrl,
        coverPdfUrl,
      });
      onSent?.(data?.notifyStatus ?? null);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Не удалось отправить");
    } finally {
      setSending(false);
    }
  };

  const total = spreadCount(numPages);
  const [left, right] = spreadPages(spread, numPages);

  // В body, а не на месте: внутри редактора (.ep-container) его стили
  // сбрасывают отступы попапа и показывают скрытые input[type=file].
  return createPortal(
    <div className="stc-overlay" onClick={onClose}>
      <div className="stc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stc-head">
          <div>
            <h3 className="stc-title">Скинуть клиенту</h3>
            {username && <div className="stc-subtitle">{username}</div>}
          </div>
          <button className="stc-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        {loadError ? (
          <div className="stc-error">{loadError}</div>
        ) : (
          <div className="stc-body">
            <div className="stc-side">
              <div className="stc-label">Версия</div>
              <div className="stc-versions">
                {ordered.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`stc-version ${selected === v.id && !bookFile ? "active" : ""}`}
                    onClick={() => {
                      setBookFile(null);
                      setSelected(v.id);
                    }}
                    disabled={sending}
                  >
                    <span className="stc-version-name">
                      {v.starred && <Star size={13} className="stc-star" fill="currentColor" />}
                      {v.editor || "Версия"}
                    </span>
                    <span className="stc-version-date">{fmtDate(v.createdAt)}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className={`stc-version ${selected === null && !bookFile ? "active" : ""}`}
                  onClick={() => {
                    setBookFile(null);
                    setSelected(null);
                  }}
                  disabled={sending}
                >
                  <span className="stc-version-name">Оригинал</span>
                  <span className="stc-version-date">исходные ответы</span>
                </button>
              </div>

              <div className="stc-label">Свои файлы</div>
              <div className="stc-files">
                <button type="button" className="stc-file-btn" onClick={() => bookInput.current?.click()} disabled={sending}>
                  {bookFile ? `Книга: ${bookFile.name}` : "Книга — свой PDF"}
                </button>
                {bookFile && (
                  <button type="button" className="stc-link" onClick={() => setBookFile(null)} disabled={sending}>
                    вернуть версию
                  </button>
                )}
                <button type="button" className="stc-file-btn" onClick={() => coverInput.current?.click()} disabled={sending}>
                  {coverFile ? `Обложка: ${coverFile.name}` : "Обложка — свой PDF"}
                </button>
                {coverFile && (
                  <button type="button" className="stc-link" onClick={() => setCoverFile(null)} disabled={sending}>
                    собрать обложку
                  </button>
                )}
                <input
                  ref={bookInput}
                  type="file"
                  accept="application/pdf"
                  hidden
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setBookFile(f);
                    e.target.value = "";
                  }}
                />
                <input
                  ref={coverInput}
                  type="file"
                  accept="application/pdf"
                  hidden
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setCoverFile(f);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>

            <div className="stc-preview">
              <div className="stc-label">Обложка</div>
              <div className="stc-cover">
                {coverUrl ? (
                  <Document file={coverUrl} loading={<div className="stc-empty">загрузка…</div>} error={<div className="stc-empty">не открылся PDF</div>}>
                    <Page pageNumber={1} height={200} renderAnnotationLayer={false} renderTextLayer={false} />
                  </Document>
                ) : (
                  <div className="stc-empty">
                    {coverState === "none"
                      ? "Клиент не выбрал обложку — можно приложить свой PDF"
                      : coverState === "error"
                        ? "Не удалось собрать обложку"
                        : "загрузка…"}
                  </div>
                )}
              </div>

              <div className="stc-label">
                Книга{numPages ? ` — ${numPages} стр.` : ""}
              </div>
              <div className="stc-book">
                {bookError ? (
                  <div className="stc-empty">{bookError}</div>
                ) : bookLoading || !bookUrl ? (
                  <div className="stc-empty">собираю PDF…</div>
                ) : (
                  <Document
                    file={bookUrl}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    loading={<div className="stc-empty">загрузка…</div>}
                    error={<div className="stc-empty">не открылся PDF</div>}
                  >
                    <div className="stc-spread">
                      <div className="stc-page">
                        {left && <Page pageNumber={left} width={210} renderAnnotationLayer={false} renderTextLayer={false} />}
                      </div>
                      <div className="stc-page">
                        {right && <Page pageNumber={right} width={210} renderAnnotationLayer={false} renderTextLayer={false} />}
                      </div>
                    </div>
                  </Document>
                )}
              </div>
              {total > 0 && (
                <div className="stc-nav">
                  <button type="button" onClick={() => setSpread((s) => Math.max(0, s - 1))} disabled={spread === 0}>
                    ←
                  </button>
                  <span>
                    {[left, right].filter(Boolean).join("–") || "—"} / {numPages}
                  </span>
                  <button type="button" onClick={() => setSpread((s) => Math.min(total - 1, s + 1))} disabled={spread >= total - 1}>
                    →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {error && <div className="stc-error">{error}</div>}

        <div className="stc-actions">
          <button className="stc-btn-ghost" onClick={onClose} disabled={sending}>
            Отмена
          </button>
          <button className="stc-btn-primary" onClick={handleSend} disabled={sending || !numPages || !!loadError}>
            {sending ? "Отправляю…" : "Скинуть клиенту"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SendToClientPopup;
